'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';

interface ExpenseEntryFormProps {
  onClose: () => void;
}

interface ExpenseFormData {
  lotNumber: string;
  category: string;
  vendor: string;
  amount: number;
  date: string;
  notes: string;
}

const CATEGORIES = [
  'Hamali (Loading)',
  'Hamali (Unloading)',
  'Labour Charges',
  'Transport / Lorry',
  'Market Fee (Tolai)',
  'Weighbridge Charges',
  'Miscellaneous',
];

export default function ExpenseEntryForm({ onClose }: ExpenseEntryFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm<ExpenseFormData>();

  const onSubmit = async (data: ExpenseFormData) => {
    setIsSubmitting(true);
    // Backend integration point: POST /api/expenses with lot_id, tenant_id, dealer_id
    await new Promise((r) => setTimeout(r, 1000));
    setIsSubmitting(false);
    toast.success(`Expense ₹${data.amount} added to Lot #${data.lotNumber}`);
    onClose();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-foreground mb-1.5">
            Lot Number <span className="text-danger">*</span>
          </label>
          <select {...register('lotNumber', { required: 'Required' })} className="input-field">
            <option value="">Select lot</option>
            {['300', '470', '410', '185', '211', '330'].map((l) => (
              <option key={`exp-lot-${l}`} value={l}>Lot #{l}</option>
            ))}
          </select>
          {errors.lotNumber && <p className="text-2xs text-danger mt-0.5">{errors.lotNumber.message}</p>}
        </div>
        <div>
          <label className="block text-xs font-semibold text-foreground mb-1.5">
            Category <span className="text-danger">*</span>
          </label>
          <select {...register('category', { required: 'Required' })} className="input-field">
            <option value="">Select category</option>
            {CATEGORIES.map((c) => (
              <option key={`exp-cat-${c}`} value={c}>{c}</option>
            ))}
          </select>
          {errors.category && <p className="text-2xs text-danger mt-0.5">{errors.category.message}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-foreground mb-1.5">
            Vendor / Payee
          </label>
          <input
            type="text"
            {...register('vendor')}
            placeholder="e.g. Ramu Labour Group"
            className="input-field"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-foreground mb-1.5">
            Amount (₹) <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            step="0.01"
            {...register('amount', {
              required: 'Required',
              min: { value: 1, message: 'Min ₹1' },
            })}
            placeholder="0.00"
            className="input-field font-mono"
          />
          {errors.amount && <p className="text-2xs text-danger mt-0.5">{errors.amount.message}</p>}
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-foreground mb-1.5">Date</label>
        <input type="date" {...register('date')} className="input-field" />
      </div>

      <div>
        <label className="block text-xs font-semibold text-foreground mb-1.5">Notes</label>
        <p className="text-2xs text-muted-foreground mb-1.5">Add calculation details or context (e.g. 150 bags × ₹21/bag)</p>
        <textarea
          {...register('notes')}
          rows={2}
          placeholder="Expense details..."
          className="input-field resize-none"
        />
      </div>

      {/* Receipt Upload */}
      <div>
        <label className="block text-xs font-semibold text-foreground mb-1.5">Receipt / Proof</label>
        <button
          type="button"
          className="w-full border-2 border-dashed border-border rounded-xl p-3 flex items-center justify-center gap-2 text-muted-foreground hover:border-primary/30 hover:text-primary transition-colors"
        >
          <Upload size={14} />
          <span className="text-xs font-medium">Upload Receipt</span>
        </button>
      </div>

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
        <button type="submit" disabled={isSubmitting} className="btn-primary flex-1">
          {isSubmitting ? (
            <><Loader2 size={14} className="animate-spin" />Saving...</>
          ) : (
            'Add Expense'
          )}
        </button>
      </div>
    </form>
  );
}